/* ==========================================================================
   Natural Nectar — mobile sticky add-to-cart

   Two jobs only:
     1. Show the bar once the real add-to-cart button has scrolled out of view.
     2. Forward taps to that real button's form.

   Everything else — variant state, quantity, AJAX, the cart notification — is
   left to Dawn's <product-form>, so there is exactly one add-to-cart code path.
   ========================================================================== */

class NNStickyATC extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-nn-satc-btn]');
    this.priceEl = this.querySelector('[data-nn-satc-price]');

    // The real buy button lives in the product form for this section.
    this.productForm = document.querySelector(
      `product-form form[data-type="add-to-cart-form"], #product-form-${this.dataset.sectionId}`
    );
    this.realButton = this.productForm?.querySelector('[type="submit"]');

    if (!this.button || !this.productForm || !this.realButton) {
      // Nothing to mirror; leaving the bar hidden is the correct failure mode.
      return;
    }

    this.button.addEventListener('click', this.onClick.bind(this));
    this.observe();
    this.watchVariant();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.unsubscribe?.();
  }

  onClick() {
    // requestSubmit() runs validation and fires the submit event that Dawn's
    // product-form.js listens for. form.submit() would skip both and do a full
    // page post, losing the AJAX cart entirely.
    if (typeof this.productForm.requestSubmit === 'function') {
      this.productForm.requestSubmit(this.realButton);
    } else {
      this.realButton.click();
    }
  }

  observe() {
    if (!('IntersectionObserver' in window)) {
      // No observer support: show the bar rather than silently withholding it.
      this.hidden = false;
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        // Reveal only once the button has been scrolled PAST — i.e. it sits
        // above the viewport. Testing `!isIntersecting` alone would also fire
        // on first load, when the button is still below the fold and the
        // shopper has not scrolled at all: the bar would cover the product
        // photo to offer an action already sitting further down the page.
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;

        this.hidden = !scrolledPast;
        this.classList.toggle('is-visible', scrolledPast);
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0 }
    );

    this.observer.observe(this.realButton);
  }

  /* Keep the bar's price and availability honest when the shopper switches
     variant. Dawn publishes the resolved variant on `variant-change`. */
  watchVariant() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId && event.data.sectionId !== this.dataset.sectionId) return;

      const variant = event?.data?.variant;
      if (!variant) return;

      if (this.priceEl && typeof variant.price === 'number') {
        this.priceEl.textContent = this.money(variant.price);
      }

      this.button.disabled = !variant.available;
      this.button.textContent = variant.available
        ? window.variantStrings?.addToCart || 'Add to cart'
        : window.variantStrings?.soldOut || 'Sold out';
    });
  }

  /* Same formatter the server used for the initial price, so switching variant
     cannot change the notation mid-page. See assets/nn-money.js. */
  money(cents) {
    if (typeof window.nnFormatMoney === 'function') {
      return window.nnFormatMoney(cents, this.dataset.moneyFormat || '');
    }
    return (cents / 100).toFixed(2);
  }
}

if (!customElements.get('nn-sticky-atc')) {
  customElements.define('nn-sticky-atc', NNStickyATC);
}
